import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: "" };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    const errorInfo = info.componentStack || "";
    this.setState({ errorInfo });

    if (import.meta.env.PROD) {
      try {
        fetch("/api/client-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {});
      } catch {}
    }

    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: "" });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-[60vh] items-center justify-center p-6" data-testid="error-boundary-fallback">
          <div className="glass-panel depth-shadow-lg p-8 max-w-md w-full text-center">
            <div className="empty-state-icon mb-4 mx-auto">
              <AlertTriangle className="h-10 w-10 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold mb-2 glow-text-sm">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mb-6">
              An unexpected error occurred. This has been reported automatically.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-4 text-left glass-panel-sm p-3 text-xs text-muted-foreground overflow-auto max-h-40">
                <summary className="cursor-pointer font-medium mb-1">Error details</summary>
                <pre className="whitespace-pre-wrap break-words">{this.state.error.message}</pre>
                {this.state.errorInfo && (
                  <pre className="whitespace-pre-wrap break-words mt-2 text-[10px] opacity-70">{this.state.errorInfo}</pre>
                )}
              </details>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleReset} variant="outline" className="gap-2" data-testid="button-try-again">
                Try Again
              </Button>
              <Button onClick={this.handleReload} className="gap-2 btn-gradient-capsule" data-testid="button-reload-page">
                <RefreshCw className="h-4 w-4" />
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
